"use client";

import { useState, Suspense } from "react";
import { Heart, Share, Receipt, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";

import { useQueryClient } from "@tanstack/react-query";
import {
  useProjectDetail,
} from "@/app/[locale]/projects/_api/use-project-queries";
import { useHasUnattributedTransactions } from "@/app/[locale]/transactions/_api/use-transaction-queries";
import { transactionQueryKeys } from "@donista/shared/query-keys/transactions";
import { useAppContext } from "@/contexts/app-context";

import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/glass-card";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareModal, type SocialPlatform } from "@/components/share-modal";
import { CompactTransactionsList } from "./compact-transactions-list";
import { CollapsibleDescription } from "@/components/collapsible-description";

import { ProjectStatistics } from "./project-statistics";
import { ProjectSupporters } from "./project-supporters";
import { CertificateGrantsAndFinancials } from "./certificate-grants-and-financials";
import { RelatedProjects } from "./related-projects";

function TransactionsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr className="border-b">
                <th className="text-left p-3 text-sm font-medium" />
                <th className="text-left p-3 text-sm font-medium" />
                <th className="text-left p-3 text-sm font-medium" />
                <th className="text-left p-3 text-sm font-medium" />
                <th className="text-left p-3 text-sm font-medium" />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b">
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </td>
                  <td className="p-3">
                    <Skeleton className="h-4 w-24" />
                  </td>
                  <td className="p-3">
                    <Skeleton className="h-5 w-20" />
                  </td>
                  <td className="p-3">
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </td>
                  <td className="p-3">
                    <Skeleton className="h-3 w-12" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const TRANSACTIONS_SECTION_ID = 'transactions';
const SHOP_BROWSER_SECTION_SELECTOR = '.shop-browser-section';

function ProjectActions({ projectId }: { projectId: string }) {
  const t = useTranslations("projects");
  const { user, updateUser } = useAppContext();
  const { data: project } = useProjectDetail(projectId);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isSupportLoading, setIsSupportLoading] = useState(false);
  // Show the one-time backfill warning only when the user actually has unattributed history to move.
  const { data: hasUnattributed = false } = useHasUnattributedTransactions({ enabled: !!user });
  const queryClient = useQueryClient();

  const handleSupportProject = async () => {
    if (!user || !project) return;

    setIsSupportLoading(true);
    try {
      await updateUser({ selectedProject: project });
      // The first selection backfills unattributed clicks server-side — refresh the gate.
      await queryClient.invalidateQueries({ queryKey: transactionQueryKeys.hasUnattributed() });
      toast.success(t("detail.successSupporting", { projectName: project.name }));
    } catch (error) {
      console.error('Error supporting project:', error);
      toast.error(t("detail.errorSupporting"));
    }
    setIsSupportLoading(false);
  };

  const socialPlatforms: SocialPlatform[] = [
    {
      name: "WhatsApp",
      icon: "message-circle",
      color: "bg-green-500",
      utmSource: "whatsapp"
    },
    {
      name: "Facebook",
      icon: "facebook",
      color: "bg-blue-600",
      utmSource: "facebook"
    },
    {
      name: "Twitter",
      icon: "twitter",
      color: "bg-blue-400",
      utmSource: "twitter"
    },
    {
      name: "Instagram",
      icon: "instagram",
      color: "bg-pink-500",
      utmSource: "instagram"
    },
    {
      name: "E-Mail",
      icon: "mail",
      color: "bg-gray-600",
      utmSource: "email"
    }
  ];

  const currentProjectUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareMessage = project
    ? t("detail.shareMessage", { projectName: project.name })
    : t("detail.share");

  const isCurrentlySupported = user?.selectedProject?.id === projectId;

  return (
    <div className="flex flex-col gap-2">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-row gap-3">
      <Button
        onClick={handleSupportProject}
        disabled={isSupportLoading || !user || isCurrentlySupported}
        className="w-full lg:flex-1 lg:w-auto rounded-xl"
        size="lg"
      >
        <Heart className={`h-4 w-4 mr-2 ${isCurrentlySupported ? 'fill-current' : ''}`} />
        {isCurrentlySupported ? t("detail.currentlySupported") : t("detail.supportProject")}
      </Button>
      <Button
        variant="glass"
        onClick={() => setIsShareOpen(true)}
        size="lg"
        className="w-full lg:w-auto rounded-xl"
      >
        <Share className="h-4 w-4 mr-2" />
        {t("detail.share")}
      </Button>
      <Button
        variant="glass"
        size="lg"
        className="w-full lg:w-auto rounded-xl"
        onClick={() => {
          const transactionsSection = document.getElementById(TRANSACTIONS_SECTION_ID);
          if (transactionsSection) {
            transactionsSection.scrollIntoView({ behavior: 'smooth' as ScrollBehavior });
          }
        }}
      >
        <Receipt className="h-4 w-4 mr-2" />
        {t("detail.viewTransactions")}
      </Button>
      <Button
        variant="glass"
        size="lg"
        className="w-full lg:w-auto rounded-xl"
        onClick={() => {
          const shopBrowserSection = document.querySelector(SHOP_BROWSER_SECTION_SELECTOR);
          if (shopBrowserSection) {
            shopBrowserSection.scrollIntoView({ behavior: 'smooth' as ScrollBehavior });
          }
        }}
      >
        <ShoppingBag className="h-4 w-4 mr-2" />
        {t("detail.supportViaShopping")}
      </Button>

      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        title={t("detail.shareTitle")}
        description={t("detail.shareDescription")}
        linkLabel={t("detail.shareLinkLabel")}
        linkPlaceholder={currentProjectUrl}
        socialPlatforms={socialPlatforms}
        shareMessage={shareMessage}
        utmCampaign="25-08_share-project"
      />
      </div>
      {user && !isCurrentlySupported && (
        <p className="text-xs text-muted-foreground">
          {t(hasUnattributed ? "detail.firstProjectNote" : "detail.futureNote")}
        </p>
      )}
    </div>
  );
}

function ProjectSidePanel({ projectId }: { projectId: string }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ProjectStatistics projectId={projectId} />
      <ProjectSupporters projectId={projectId} />
    </div>
  );
}

export function ProjectDetailClient({ projectId }: { projectId: string }) {
  const t = useTranslations("projects");
  const { data: projectDetail } = useProjectDetail(projectId);

  return (
    <>
      <ProjectActions projectId={projectId} />

      {projectDetail?.description && (
        <GlassCard>
          <CollapsibleDescription description={projectDetail.description} title={t("detail.aboutProject")} />
        </GlassCard>
      )}

      <ProjectSidePanel projectId={projectId} />

      <GlassCard>
        <div id="transactions">
          <h2 className="text-xl font-semibold mb-4">{t("detail.latestTransactions")}</h2>
          <p className="text-muted-foreground mb-6">
            {t("detail.latestTransactionsDescription")}
          </p>
          <Suspense fallback={<TransactionsSkeleton />}>
            <CompactTransactionsList params={{ projectId }} emptyMessage={t("detail.noTransactionsForProject")} linkToShop hideProject />
          </Suspense>
        </div>
      </GlassCard>

      <CertificateGrantsAndFinancials projectId={projectId} />

      <RelatedProjects projectId={projectId} />
    </>
  );
}
